import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Icon } from '@/components/ui/Icon/Icon';
import { ApiRequestError } from '@/services/api';
import { useEditGalleryCaption } from '@/services/gallery';
import { nb } from '@/i18n/nb';
import type { GalleryImage } from '@/types/api';
import s from './GalleryViewerOverlay.module.css';

type Props = {
  image: GalleryImage;
  /** Rediger-modus on AND the viewer is logged-in: gates the edit/delete affordances. */
  editMode: boolean;
  onClose: () => void;
  /** Push the server's updated row back up so the open viewer reflects the new caption. */
  onUpdated: (image: GalleryImage) => void;
  /** Parent owns the confirm Dialog (it must layer above this viewer). */
  onRequestDelete: () => void;
};

/*
 * Galleri's full-surface image viewer (wireframe viewer-overlay / viewer-with-delete).
 * Built bespoke (not via <Modal>) for the same reasons as Vedlikehold's
 * ImageViewerOverlay — fullscreen image, no modal chrome — but richer: it shows the
 * caption + attribution and, in Rediger-modus, an inline caption edit (saves on blur,
 * per product-spec) + a "Slett" trigger. Same a11y skeleton: portal + focus trap +
 * Esc + backdrop-click-closes.
 *
 * z-index sits below <Modal> (50) on purpose so the parent's delete-confirm Dialog
 * layers on top of this viewer rather than behind it.
 */
export function GalleryViewerOverlay({ image, editMode, onClose, onUpdated, onRequestDelete }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const editCaption = useEditGalleryCaption();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(image.caption ?? '');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // editingRef lets the document-level Esc handler + backdrop check read the latest
  // editing state without re-binding the listener on every keystroke.
  const editingRef = useRef(editing);
  editingRef.current = editing;

  // Esc cancels an in-progress caption edit first; only when not editing does it close
  // the viewer (so a stray Esc mid-edit doesn't blow away the whole overlay).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (editingRef.current) {
        setEditing(false);
        setDraft(image.caption ?? '');
        setError(null);
      } else {
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, image.caption]);

  // Move focus into the caption field the moment edit mode opens.
  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  function startEdit() {
    setDraft(image.caption ?? '');
    setError(null);
    setEditing(true);
  }

  // Commit the caption (called on blur or Enter). No-op when unchanged; validates the
  // ≤256 non-empty contract client-side (the server re-validates regardless).
  function save() {
    const next = draft.trim();
    if (next.length === 0) {
      setError(nb.galleri.uploadModal.validation.captionRequired);
      return;
    }
    if (next.length > 256) {
      setError(nb.galleri.uploadModal.validation.captionMax);
      return;
    }
    if (next === image.caption) {
      setEditing(false);
      return;
    }
    editCaption.mutate(
      { id: image.id, caption: next },
      {
        onSuccess: (res) => {
          setEditing(false);
          onUpdated(res.data);
        },
        onError: (e) =>
          setError(e instanceof ApiRequestError ? e.message : nb.galleri.viewer.saveError),
      },
    );
  }

  return createPortal(
    <div
      className={s.backdrop}
      onPointerDown={(e) => {
        // Backdrop click closes — but not while editing (don't lose an in-progress caption).
        if (e.target === e.currentTarget && !editingRef.current) onClose();
      }}
    >
      <div
        ref={trapRef}
        className={s.viewer}
        role="dialog"
        aria-modal="true"
        aria-label={nb.galleri.viewer.label}
      >
        <button
          type="button"
          className={s.close}
          onClick={onClose}
          aria-label={nb.galleri.viewer.closeAria}
        >
          <Icon name="x" size={24} />
        </button>

        {/* viewer variant maxes at 1200² (ImagePipelineService); contain centres
            portrait + landscape without cropping. Caption is the alt text. */}
        <img className={s.image} src={image.viewerUrl} alt={image.caption ?? ''} />

        <div className={s.meta}>
          {editing ? (
            <div className={s.captionEdit}>
              <textarea
                ref={textareaRef}
                className={s.captionInput}
                rows={2}
                maxLength={256}
                value={draft}
                aria-label={nb.galleri.viewer.captionEditAria}
                placeholder={nb.galleri.viewer.captionPlaceholder}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={save}
                onKeyDown={(e) => {
                  // Enter commits (captions are short); Shift+Enter still allows a newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    save();
                  }
                }}
              />
              <span className={s.counter}>{draft.length} / 256</span>
            </div>
          ) : (
            <p className={s.caption}>{image.caption}</p>
          )}

          <p className={s.attribution}>
            {nb.galleri.contributedBy} {image.uploadedByName}
          </p>

          {error && (
            <p role="alert" className={s.error}>
              {error}
            </p>
          )}

          {editMode && !editing && (image.canEdit || image.canDelete) && (
            <div className={s.actions}>
              {image.canEdit && (
                <button type="button" className={s.actionBtn} onClick={startEdit}>
                  <Icon name="pencil" size={16} /> {nb.galleri.viewer.editCaption}
                </button>
              )}
              {image.canDelete && (
                <button
                  type="button"
                  className={s.actionBtn}
                  data-variant="danger"
                  onClick={onRequestDelete}
                >
                  <Icon name="trash" size={16} /> {nb.galleri.viewer.delete}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
